/**
 * WhatsApp header media — slice 9, "Rich WhatsApp templates".
 *
 * Slice 7 built the *shape* of a media header and could submit one: the
 * document carries a `mediaHandle`, `meta-template-payload.ts` nests it under
 * `example.header_handle`, and `wa-template-document.ts` round-trips it to
 * `media_handle` on disk. What nothing could do was **produce** a handle. This
 * file is the vocabulary of producing one, and of everything that follows from
 * the fact that a handle is not ours.
 *
 * Four claims shape every export below.
 *
 * ## 1. The bytes are ours. The handle is a perishable pointer at them.
 *
 * Meta's Resumable Upload issues a handle that is valid for template
 * **creation** and nothing else. It cannot be re-minted from itself, and Meta
 * gives no way to read the file back. So a template that was rejected, whose
 * bytes we did not keep, is a template that can never be resubmitted — the
 * person would have to find the original PDF again, and the one thing certain
 * about a marketing team's original PDF is that nobody can find it.
 *
 * Therefore the file goes in `template_assets` under the `wa_header_media`
 * kind, and the handle is stored **beside** it — {@link WaHeaderMediaAsset}
 * carries `reviewHandle` as a nullable field on the asset, never as a
 * replacement for it. `null` there means "no handle has been minted from these
 * bytes yet", which is a different statement from "the upload failed" and a
 * different statement again from "the file is missing"; only the last of those
 * makes a template unfixable.
 *
 * The rejected alternative was the obvious one: store the handle on the
 * document and nothing else, which is what slice 7's shape looks like if you
 * read it alone. It is one column lighter and it loses the file.
 *
 * ## 2. The review handle and the send media are different things, and this
 * file makes them non-interchangeable rather than merely documented.
 *
 * They come from different endpoints, mean different things and expire on
 * different clocks:
 *
 *   - **{@link WaReviewHandle}** — `POST /<app-id>/uploads` then
 *     `POST /upload:<session>`, which answers `{ "h": "..." }`. It is the
 *     *sample* Meta's reviewer looks at. It goes in `example.header_handle` on
 *     the create-template body and is used exactly once, at submission.
 *   - **{@link WaSendMediaId}** — `POST /<phone-number-id>/media`, which answers
 *     `{ "id": "..." }`. It is the media an actual message carries, in
 *     `components[].parameters[].image.id`. Meta keeps it for 30 days.
 *
 * Conflating them produces a template that approves and then sends nothing —
 * the failure class slice 4's preview work exists to prevent, arriving from a
 * new direction. So they are not two strings. Each is a one-field object with
 * its own `use` literal and its own value field (`handle` versus `mediaId`),
 * on the {@link import('./analytics.js').Reach}/`RowCount` precedent from slice
 * 14: mutually non-assignable in both directions, and neither assignable from a
 * bare string. **There is deliberately no function in this file that turns one
 * into the other**, and there must not be one: the whole guarantee is that
 * `waMediaHeaderForReview` cannot be handed a send id and
 * `waSendHeaderMedia` cannot be handed a review handle.
 *
 * The rejected alternative was branded strings — `string & { __review: true }`.
 * It reads better and it does not survive the wire: the brand is erased in
 * JSON, so the API would re-hydrate a send id into a review handle by parsing
 * it, which is precisely the swap the brand was there to stop.
 *
 * ## 3. Meta's limits are Meta's, they differ per format, and they are not
 * slice 4's.
 *
 * `MAX_ATTACHMENT_BYTES` is 10 MB because SES and Gmail say so. None of that
 * applies here. The numbers below were looked up rather than inherited, and
 * {@link WA_MEDIA_LIMITS} states, per format, which figure is Meta's ceiling and
 * which is the narrower one Wend actually holds — the same honesty
 * `WA_MAX_BUTTONS` practises in `./whatsapp.js`, where 3 is "the narrower of two
 * true things" rather than a claim about Meta.
 *
 * ## 4. The refusal happens before an upload session is opened.
 *
 * {@link findWaHeaderMediaRejection} is a pure function of format, MIME type and
 * byte count. It runs on bytes we already hold, answers in the sentence the
 * person reads, and names the limit that was actually applied — including
 * whose limit it is. Opening a Meta upload session for a file we already know
 * Meta will refuse spends a round trip to learn something arithmetic knew.
 *
 * ## Position in the package
 *
 * A **leaf**. It imports `../common.js`, `../enums.js` and `./whatsapp.js` —
 * the last for {@link WaMediaFormat} and {@link WaMediaHeader}, which belong
 * beside the header they discriminate — and nothing imports it back.
 * `./whatsapp.js` is already in a cycle with `./template.js`; an edge from
 * there into here would close a second one between two modules that both build
 * zod schemas at load, which fails at import with `z.enum(undefined)` rather
 * than at review. The one reference pointing the other way is a `{@link}` in a
 * JSDoc comment, which is erased.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import type { TemplateAssetKind } from '../enums.js';
import {
  WA_MEDIA_FORMAT_VALUES,
  waMediaFormatSchema,
  type WaMediaFormat,
  type WaMediaHeader,
} from './whatsapp.js';

/* -------------------------------------------------------------------------- */
/* The asset kind                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The `template_assets.kind` a WhatsApp header file is stored under.
 *
 * It is `'wa_header_media'` and **not** `'attachment'`, which is decision 1 of
 * the slice and is argued at length in `../enums.js`: both are referenced by no
 * block, so a sweep that cannot tell them apart is a sweep that deletes the
 * wrong one.
 *
 * The literal is written out and then *checked against* the vocabulary on the
 * line below, rather than read out of the tuple by index. Indexing would also
 * prevent a typo, but it makes the value invisible at the definition and it
 * silently follows a re-ordering of the enum — which is the one edit that must
 * never happen, because a pg enum remembers the order its labels were created
 * in. The assignment below fails to compile if the spelling ever diverges.
 */
export const WA_HEADER_MEDIA_ASSET_KIND = 'wa_header_media' as const;
export type WaHeaderMediaAssetKind = typeof WA_HEADER_MEDIA_ASSET_KIND;

/** Fails to compile if `../enums.js` stops spelling the kind this way. */
const _waHeaderMediaKindIsInTheVocabulary: TemplateAssetKind = WA_HEADER_MEDIA_ASSET_KIND;
void _waHeaderMediaKindIsInTheVocabulary;

/* -------------------------------------------------------------------------- */
/* Meta's limits — what was confirmed, and what was not                        */
/* -------------------------------------------------------------------------- */

const MB = 1024 * 1024;

/**
 * **CONFIRMED**, read off Meta's own documentation on 2026-09-06:
 *
 *   - *Cloud API → Media → Supported Media Types and Maximum File Sizes*
 *     (`developers.facebook.com/docs/whatsapp/cloud-api/reference/media`):
 *     image **5 MB** (`image/jpeg`, `image/png`; "Images must be 8-bit, RGB or
 *     RGBA"), video **16 MB** (`video/mp4`, `video/3gpp`; H.264 + AAC, single
 *     audio stream or none), document **100 MB** (PDF, plain text and the
 *     Office formats). The page also states a platform-wide 100 MB ceiling on
 *     media messages.
 *   - *Graph API → Resumable Upload* (`docs/graph-api/guides/upload`): the
 *     `file_type` parameter accepts **`application/pdf`, `image/jpeg`,
 *     `image/jpg`, `image/png` and `video/mp4`** — and this is the endpoint that
 *     mints a header handle, so it, not the send-side table, is what
 *     {@link WA_MEDIA_LIMITS} allows. That list is *narrower* than what a
 *     message may carry: a `.docx` is a perfectly good WhatsApp document and
 *     cannot be a template header sample, and `image/webp` — which slice 4
 *     accepts as an inline email image — is not on it at all.
 *   - Media uploaded for sending persists **30 days**; media ids from the API
 *     expire after 30 days, ids arriving on an inbound webhook after 7.
 *
 * **GUESSED, and marked as such rather than presented as fact** — stated
 * because the alternative is a number in the code that reads as authority:
 *
 *   - **A review handle's lifetime.** Meta documents that the handle is used to
 *     publish the file and does not, anywhere we could find, publish an expiry.
 *     {@link WA_REVIEW_HANDLE_ASSUMED_TTL_HOURS} is therefore a *policy*, not a
 *     fact: mint fresh on every submission and never reuse one across a
 *     rejection. The cost of being wrong in this direction is one extra upload
 *     of a file we already hold. The cost of being wrong the other way is a
 *     submission rejected for a stale handle, which is a mark against the WABA.
 *   - **The Resumable Upload API's own size ceiling.** The page documents no
 *     per-file maximum. The send-side figures are used as the ceiling on the
 *     assumption that Meta will not review a sample it would refuse to deliver.
 *
 * Meta's `image/jpg` is a non-standard alias and is deliberately absent from
 * every allow-list below: nothing sniffing bytes produces it, and admitting it
 * would mean two spellings of one type in a `Record` keyed by MIME.
 */
export const WA_MEDIA_LIMITS_SOURCE =
  "Meta's Cloud API media reference and Graph API Resumable Upload guide, read 2026-09-06.";

/**
 * How long Wend treats a minted review handle as usable before minting another.
 *
 * A guess, and the header above says so. It is short on purpose: re-uploading a
 * file we already have on disk costs one Graph round trip, and the failure it
 * avoids costs a rejection on the tenant's WABA.
 */
export const WA_REVIEW_HANDLE_ASSUMED_TTL_HOURS = 24;

/** Meta keeps media uploaded for sending for 30 days. Confirmed; see above. */
export const WA_SEND_MEDIA_RETENTION_DAYS = 30;

/**
 * Everything that differs per format, in one table.
 *
 * `metaMaxBytes` is Meta's documented ceiling. `maxBytes` is what Wend actually
 * enforces, and it is never larger. They are two fields rather than one because
 * a single number cannot answer the question a refusal message has to answer —
 * *whose* limit did I just hit — and a message that attributes Wend's caution to
 * WhatsApp is a false claim about a provider, which this codebase has shipped
 * before and does not intend to again.
 *
 * They differ for exactly one format. A document may be 100 MB at Meta and is
 * held to 20 MB here, because the upload route buffers the whole file in memory
 * to sniff it, hash it and hand it to the Graph transport — the same constraint
 * that fixes slice 1's `MAX_UPLOAD_BYTES` at 20 MB, arrived at independently
 * rather than imported, since a spreadsheet limit is not a WhatsApp limit and
 * the two are free to move apart.
 *
 * `mimeTypes` is the Resumable Upload API's accepted set narrowed to one
 * spelling per type. It is **not** slice 4's list and must not be replaced by
 * it: `TEMPLATE_IMAGE_MIME_TYPES` admits GIF and WebP, which this endpoint
 * refuses, and `TEMPLATE_ATTACHMENT_MIME_TYPES` admits nine document types Meta
 * will not take as a header sample.
 */
export interface WaMediaFormatLimits {
  readonly format: WaMediaFormat;
  /** What Meta's Resumable Upload will accept as `file_type`, canonical spellings. */
  readonly mimeTypes: readonly string[];
  /** Extensions for the picker's `accept`. Display only — the bytes decide the type. */
  readonly extensions: readonly string[];
  /** Meta's documented ceiling. Confirmed. */
  readonly metaMaxBytes: number;
  /** What Wend enforces. Never above {@link metaMaxBytes}. */
  readonly maxBytes: number;
  /** Whose number {@link maxBytes} is. Decides what a refusal is allowed to claim. */
  readonly limitOwner: 'meta' | 'wend';
  /** The word the composer and the refusal use. Lower case, mid-sentence. */
  readonly noun: string;
}

export const WA_MEDIA_LIMITS: Readonly<Record<WaMediaFormat, WaMediaFormatLimits>> = {
  image: {
    format: 'image',
    mimeTypes: ['image/jpeg', 'image/png'],
    extensions: ['.jpg', '.jpeg', '.png'],
    metaMaxBytes: 5 * MB,
    maxBytes: 5 * MB,
    limitOwner: 'meta',
    noun: 'image',
  },
  document: {
    format: 'document',
    mimeTypes: ['application/pdf'],
    extensions: ['.pdf'],
    metaMaxBytes: 100 * MB,
    maxBytes: 20 * MB,
    limitOwner: 'wend',
    noun: 'document',
  },
  video: {
    format: 'video',
    mimeTypes: ['video/mp4'],
    extensions: ['.mp4'],
    metaMaxBytes: 16 * MB,
    maxBytes: 16 * MB,
    limitOwner: 'meta',
    noun: 'video',
  },
};

/**
 * The largest file any WA header format accepts, for the one place that has to
 * choose a multer ceiling before it knows the format.
 *
 * Derived from the table, so a format whose cap rises cannot leave this behind.
 * It is a backstop and not the check: the real refusal is per format, in
 * {@link findWaHeaderMediaRejection}, and a 19 MB PNG passes this and is still
 * refused there with the 5 MB figure a person can act on.
 */
export const WA_HEADER_MEDIA_MAX_BYTES = Math.max(
  ...WA_MEDIA_FORMAT_VALUES.map((format) => WA_MEDIA_LIMITS[format].maxBytes),
);

/** Every MIME type any WA header format accepts. Derived, never retyped. */
export const WA_HEADER_MEDIA_MIME_TYPES: readonly string[] = WA_MEDIA_FORMAT_VALUES.flatMap(
  (format) => WA_MEDIA_LIMITS[format].mimeTypes,
);

/** The `accept` attribute for one format's file picker. */
export function waHeaderMediaAccept(format: WaMediaFormat): string {
  const limits = WA_MEDIA_LIMITS[format];
  return [...limits.mimeTypes, ...limits.extensions].join(',');
}

/**
 * Which header format a sniffed MIME type belongs to, or `null` for one no
 * format accepts.
 *
 * Returns `null` rather than throwing, and the name says it can fail. It is the
 * cross-check on the format the client declared: an upload that says `image` and
 * whose bytes sniff as `video/mp4` is refused by
 * {@link findWaHeaderMediaRejection} naming both, rather than stored under a
 * ceiling that was chosen for the wrong thing.
 */
export function waMediaFormatForMimeType(mimeType: string): WaMediaFormat | null {
  const normalised = mimeType.trim().toLowerCase();
  for (const format of WA_MEDIA_FORMAT_VALUES) {
    if (WA_MEDIA_LIMITS[format].mimeTypes.includes(normalised)) return format;
  }
  return null;
}

/** `5 MB`, `1.5 MB`. One decimal only when it is not a whole number. */
export function formatWaMediaBytes(bytes: number): string {
  const megabytes = bytes / MB;
  const rounded = Math.round(megabytes * 10) / 10;
  return `${Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)} MB`;
}

/** `5,250,000 bytes`. Grouped by hand — no `Intl`, so every runtime agrees. */
function formatExactBytes(bytes: number): string {
  const digits = String(Math.trunc(bytes));
  return `${digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} bytes`;
}

/**
 * A file's size and the limits it is being measured against, rendered so that
 * **the file can never read as being the same size as the limit it exceeded**.
 *
 * `formatWaMediaBytes` rounds to one decimal, which is right for a limit — *5
 * MB* is how Meta publishes it and how a person holds it in their head — and
 * produces a self-contradicting sentence for anything just over one: every file
 * from 5,242,881 to 5,295,308 bytes rendered as *"That image is 5 MB. WhatsApp's
 * documented limit for an image is 5 MB"*, refusing a file for exceeding a
 * figure it was said to equal, with no number in the message to act on. The
 * bands are real and reachable — a 5.006 MB PNG is an ordinary export — and the
 * browser gate renders the identical string, so the person was told it twice.
 *
 * So the rounding stays until it collides, and then **every figure in the
 * sentence** drops to exact bytes together. Mixing the two units inside one
 * sentence would trade a contradiction for an apples-and-oranges comparison,
 * which is the same defect wearing better clothes.
 */
function waMediaSizeFigures(
  byteSize: number,
  maxBytes: number,
  metaMaxBytes: number,
): { size: string; cap: string; metaCap: string } {
  const size = formatWaMediaBytes(byteSize);
  const cap = formatWaMediaBytes(maxBytes);
  if (size !== cap) return { size, cap, metaCap: formatWaMediaBytes(metaMaxBytes) };
  return {
    size: formatExactBytes(byteSize),
    cap: formatExactBytes(maxBytes),
    metaCap: formatExactBytes(metaMaxBytes),
  };
}

/* -------------------------------------------------------------------------- */
/* The refusal — decided before any upload session is opened                   */
/* -------------------------------------------------------------------------- */

export const WA_MEDIA_REJECTION_VALUES = [
  'empty_file',
  'unsupported_type',
  'wrong_format_for_type',
  'too_large',
] as const;
export const waMediaRejectionReasonSchema = z.enum(WA_MEDIA_REJECTION_VALUES);
export type WaMediaRejectionReason = z.infer<typeof waMediaRejectionReasonSchema>;

/**
 * Why a file cannot be a header for the format it was offered as.
 *
 * A discriminated union rather than a string, because each arm carries the
 * figures the sentence needs and a bare message cannot be re-worded by the
 * front-end without the numbers drifting from the check that produced them.
 *
 * `wrong_format_for_type` is a member in its own right and not folded into
 * `unsupported_type`: "that is not a file WhatsApp takes" and "that is a
 * perfectly good WhatsApp video and you offered it as an image header" are
 * different problems with different fixes, and collapsing them tells somebody
 * to convert a file that needed no converting.
 */
export type WaMediaRejection =
  | { readonly reason: 'empty_file'; readonly format: WaMediaFormat }
  | {
      readonly reason: 'unsupported_type';
      readonly format: WaMediaFormat;
      readonly mimeType: string;
    }
  | {
      readonly reason: 'wrong_format_for_type';
      readonly format: WaMediaFormat;
      readonly mimeType: string;
      /** The format this file *would* be accepted as. */
      readonly actualFormat: WaMediaFormat;
    }
  | {
      readonly reason: 'too_large';
      readonly format: WaMediaFormat;
      readonly byteSize: number;
      /** The limit that was applied — {@link WaMediaFormatLimits.maxBytes}. */
      readonly maxBytes: number;
      /** Meta's own ceiling, so the message can say when the two differ. */
      readonly metaMaxBytes: number;
      readonly limitOwner: 'meta' | 'wend';
    };

/** What a caller offers for checking. All three known before a byte is sent to Meta. */
export interface WaHeaderMediaCandidate {
  /** The format the composer declared, which chose the ceiling. */
  readonly format: WaMediaFormat;
  /** Sniffed from the bytes, never read off the multipart `Content-Type` header. */
  readonly mimeType: string;
  readonly byteSize: number;
}

/**
 * `null` when the file is acceptable; the reason when it is not.
 *
 * Pure, and deliberately answerable without a network call — done-when 3 of the
 * slice is that a file Meta will not take is refused *before* an upload session
 * exists. The order of the checks is the order a person can act on: an empty
 * file first, then whether the file is one WhatsApp takes at all, then whether
 * it matches the header slot it was offered for, and only then the size.
 * Reporting "too large" about a file that is also the wrong type sends somebody
 * to compress a `.gif` that was never going to be accepted.
 */
export function findWaHeaderMediaRejection(
  candidate: WaHeaderMediaCandidate,
): WaMediaRejection | null {
  const { format } = candidate;
  const limits = WA_MEDIA_LIMITS[format];

  if (candidate.byteSize <= 0) {
    return { reason: 'empty_file', format };
  }

  const mimeType = candidate.mimeType.trim().toLowerCase();
  const actualFormat = waMediaFormatForMimeType(mimeType);
  if (actualFormat === null) {
    return { reason: 'unsupported_type', format, mimeType };
  }
  if (actualFormat !== format) {
    return { reason: 'wrong_format_for_type', format, mimeType, actualFormat };
  }

  if (candidate.byteSize > limits.maxBytes) {
    return {
      reason: 'too_large',
      format,
      byteSize: candidate.byteSize,
      maxBytes: limits.maxBytes,
      metaMaxBytes: limits.metaMaxBytes,
      limitOwner: limits.limitOwner,
    };
  }

  return null;
}

/**
 * The sentence a person reads.
 *
 * Shipped from the contract rather than typed into a component, for the reason
 * `REACH_EXPLAINER` is: the numbers in the sentence and the numbers in the check
 * are then the same numbers. A limit quoted in an error message has to be the
 * limit that was actually applied — the whole argument of
 * `asset-upload-error.interceptor.ts`, which exists because quoting slice 1's
 * 20 MB at somebody who hit slice 4's 2 MB was both wrong and unactionable.
 *
 * When Wend holds a figure narrower than Meta's, the sentence says so and says
 * whose is whose. Attributing our caution to WhatsApp would be a confident claim
 * about a provider that is not true.
 *
 * The size sentences say *"WhatsApp's limit for an image is 5 MB"* rather than
 * *"WhatsApp's upload refuses an image over 5 MB"*, and the difference is the
 * one this file's own limits header is careful about: Meta publishes those
 * figures for media on the messaging endpoints, and publishes **no** size
 * ceiling for the Resumable Upload that mints a header handle. Applying them
 * there is a documented assumption. A message may state the documented figure;
 * it may not state an assumption about a provider's endpoint as fact.
 *
 * `unsupported_type` names the narrowing rather than saying "WhatsApp will not
 * take this", because for a `.docx` or a `.3gp` that flat claim is false —
 * WhatsApp sends both happily and refuses both as a template header sample. A
 * sentence that sends somebody to convert a file they did not need to convert
 * costs the same support ticket as no message at all.
 */
export function describeWaMediaRejection(rejection: WaMediaRejection): string {
  const limits = WA_MEDIA_LIMITS[rejection.format];
  switch (rejection.reason) {
    case 'empty_file':
      return `That file is empty, so there is nothing to put behind the ${limits.noun} header.`;
    case 'unsupported_type':
      return `A template header sample cannot be ${rejection.mimeType}. WhatsApp accepts a narrower set here than it does in a message: ${anArticle(limits.noun)} ${limits.noun} header must be ${listTypes(limits.mimeTypes)}.`;
    case 'wrong_format_for_type': {
      const actual = WA_MEDIA_LIMITS[rejection.actualFormat];
      return `That is ${anArticle(rejection.mimeType)} ${rejection.mimeType} file, which WhatsApp takes as a ${actual.noun} header, not as ${anArticle(limits.noun)} ${limits.noun} header. Change the header format to ${actual.noun}, or choose a file that is ${listTypes(limits.mimeTypes)}.`;
    }
    case 'too_large': {
      // One figure per number in the sentence, and never two units in one — see
      // `waMediaSizeFigures`, which is what stops a file being described as
      // exactly the size of the limit it exceeded.
      const { size, cap, metaCap } = waMediaSizeFigures(
        rejection.byteSize,
        rejection.maxBytes,
        rejection.metaMaxBytes,
      );
      if (rejection.limitOwner === 'meta') {
        return `That ${limits.noun} is ${size}. WhatsApp's documented limit for ${anArticle(limits.noun)} ${limits.noun} is ${cap}, so it is refused here rather than after an upload that would fail.`;
      }
      return `That ${limits.noun} is ${size}. Wend accepts ${anArticle(limits.noun)} ${limits.noun} header up to ${cap}. WhatsApp's own documented limit is ${metaCap}; the smaller figure is ours, because the whole file is held in memory while it is checked and uploaded.`;
    }
  }
}

/** `image/jpeg or image/png`; `application/pdf` on its own. No article. */
function listTypes(mimeTypes: readonly string[]): string {
  if (mimeTypes.length <= 1) return mimeTypes[0] ?? 'a type WhatsApp accepts';
  const last = mimeTypes[mimeTypes.length - 1] ?? '';
  return `${mimeTypes.slice(0, -1).join(', ')} or ${last}`;
}

function anArticle(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}

/* -------------------------------------------------------------------------- */
/* Two handles that must never be swapped                                      */
/* -------------------------------------------------------------------------- */

/**
 * The marker on a handle that is only good for **template creation**.
 *
 * One member, and there will not be a second: a handle that is valid for
 * something else is not a flavour of review handle, it is a different string
 * with a different lifetime.
 */
export const WA_REVIEW_HANDLE_USE = 'template_review' as const;

/** The marker on media that is only good for **one outbound message**. */
export const WA_SEND_MEDIA_USE = 'outbound_send' as const;

/** Printed wherever both appear, so the screen never calls them the same thing. */
export const WA_REVIEW_HANDLE_EXPLAINER =
  'The sample WhatsApp reviews when the template is submitted. It is minted from the file stored here and is not what a message carries.';

export const WA_SEND_MEDIA_EXPLAINER =
  'The media one outbound message carries. Uploaded separately from the same stored file, and kept by WhatsApp for 30 days.';

/**
 * **The handle Meta's Resumable Upload returned**, for use in
 * `example.header_handle` and nowhere else.
 *
 * A one-field object with a `use` literal, not a branded string, for the reason
 * argued in this file's header: a brand does not survive JSON, and JSON is
 * exactly where the swap would happen. `mintedAt` is on the shape rather than
 * inferred from the asset's `uploadedAt` because the two genuinely differ — the
 * file is uploaded once and the handle is re-minted on every resubmission, which
 * is the fact that makes a rejected template fixable.
 */
export const waReviewHandleSchema = z
  .object({
    /** Always {@link WA_REVIEW_HANDLE_USE}. The discriminator against a send id. */
    use: z.literal(WA_REVIEW_HANDLE_USE),
    /** Meta's opaque `h`. Bounded to match `waMediaHeaderSchema.mediaHandle`. */
    handle: z.string().trim().min(1).max(512),
    mintedAt: isoDateTimeSchema,
  })
  .strict();
export type WaReviewHandle = z.infer<typeof waReviewHandleSchema>;

/** The only supported way to make one. */
export function waReviewHandle(handle: string, mintedAt: string): WaReviewHandle {
  return { use: WA_REVIEW_HANDLE_USE, handle: handle.trim(), mintedAt };
}

/**
 * **The media id `POST /<phone-number-id>/media` returned**, for use in a send's
 * `components` and nowhere else.
 *
 * Its value field is `mediaId`, not `handle`, so even structural typing cannot
 * mistake the two: they share no field but the discriminator, and the
 * discriminator disagrees.
 */
export const waSendMediaIdSchema = z
  .object({
    /** Always {@link WA_SEND_MEDIA_USE}. The discriminator against a review handle. */
    use: z.literal(WA_SEND_MEDIA_USE),
    /** Meta's `id`. Expires {@link WA_SEND_MEDIA_RETENTION_DAYS} days after upload. */
    mediaId: z.string().trim().min(1).max(512),
    uploadedAt: isoDateTimeSchema,
  })
  .strict();
export type WaSendMediaId = z.infer<typeof waSendMediaIdSchema>;

/** The only supported way to make one. */
export function waSendMediaId(mediaId: string, uploadedAt: string): WaSendMediaId {
  return { use: WA_SEND_MEDIA_USE, mediaId: mediaId.trim(), uploadedAt };
}

/**
 * Both, where a screen or a log has to say which it is holding.
 *
 * A union is safe; a *conversion* is not, and there is none in this file. If one
 * is ever wanted, the thing wanted is a second upload from the stored bytes —
 * {@link WaHeaderMediaAsset} exists so that is always possible.
 */
export const waMediaReferenceSchema = z.discriminatedUnion('use', [
  waReviewHandleSchema,
  waSendMediaIdSchema,
]);
export type WaMediaReference = z.infer<typeof waMediaReferenceSchema>;

/* -------------------------------------------------------------------------- */
/* The stored asset — the bytes, with the handle beside them                   */
/* -------------------------------------------------------------------------- */

/**
 * A WhatsApp header file as the API reports it: one `template_assets` row of
 * kind `wa_header_media`, plus whichever handle has been minted from it.
 *
 * `url` is an **API path** — tenant-scoped, authenticated, served by us —
 * exactly as `templateAssetSchema.url` is, and for the same reason: the bytes
 * being ours is the entire point of the slice, and handing out a storage URL
 * would give that back.
 *
 * `reviewHandle` is nullable and the null is meaningful. It says *no handle has
 * been minted from these bytes*, which is a normal state — an uploaded file
 * waiting for a submission is in it — and it is not the same as a handle that
 * was minted and has since gone stale. Nothing may render it as "upload
 * failed", and nothing may treat its absence as a missing file: the file is the
 * row.
 *
 * There is **no `sendMediaId` field here on purpose.** Send media is per
 * message, uploaded on the send path, and lives 30 days; caching one on the
 * authoring row would produce exactly the confusion this file is built to
 * prevent, and would go stale silently — an expired id fails at Meta, on a
 * campaign, one recipient at a time.
 *
 * **`format` and `mimeType` are refined against each other**, so an asset whose
 * pair disagrees fails to parse — including on the way *out*, which means an API
 * that stored a `.pdf` as an `image` header fails parsing its own response
 * rather than shipping a template Meta rejects at submission.
 *
 * **`byteSize` is deliberately NOT refined against the format's cap**, and that
 * asymmetry is a decision rather than an oversight. The MIME allow-list is
 * Meta's and only ever widens; a byte cap is ours and may well come *down*, and
 * a refinement on it would turn every already-stored file above the new figure
 * into a row the composer cannot render at all — the template becomes
 * un-openable rather than un-submittable. The cap is enforced at upload, where
 * refusing costs one file, by {@link findWaHeaderMediaRejection}.
 */
export const waHeaderMediaAssetSchema = z
  .object({
    assetId: uuidSchema,
    /** Which header slot these bytes are for. Fixes the ceiling and the MIME set. */
    format: waMediaFormatSchema,
    /** The uploaded name, for display and for Meta's `file_name`. */
    filename: z.string().trim().min(1).max(255),
    /** Sniffed from the bytes at upload. One of the format's `mimeTypes`. */
    mimeType: z.string().trim().min(1),
    byteSize: z.number().int().positive(),
    /** API path, e.g. `/api/templates/<id>/whatsapp/header-media`. Never storage. */
    url: z.string(),
    uploadedAt: isoDateTimeSchema,
    /** The handle minted from these bytes, or `null` — see above. */
    reviewHandle: waReviewHandleSchema.nullable(),
  })
  .strict()
  .superRefine((asset, ctx) => {
    if (waMediaFormatForMimeType(asset.mimeType) === asset.format) return;
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['mimeType'],
      message: `A ${asset.format} header cannot be ${asset.mimeType}. WhatsApp accepts ${listTypes(WA_MEDIA_LIMITS[asset.format].mimeTypes)} there.`,
    });
  });
export type WaHeaderMediaAsset = z.infer<typeof waHeaderMediaAssetSchema>;

/**
 * Whether a stored handle is old enough that a submission should mint a fresh
 * one first.
 *
 * The TTL is a **guess** and this function's name does not pretend otherwise —
 * it says "should re-mint", not "has expired". Meta publishes no expiry for a
 * review handle. Re-minting an unexpired handle costs one upload of a file we
 * already hold; submitting with a stale one costs a rejection on the tenant's
 * WABA, so the asymmetry decides which way to be wrong.
 *
 * A `null` handle answers `true`, which is the same answer for a different
 * reason and the reason callers do not need to distinguish: both mean "mint one
 * before submitting".
 */
export function shouldRemintReviewHandle(
  handle: WaReviewHandle | null,
  now: Date = new Date(),
): boolean {
  if (handle === null) return true;
  const mintedAt = Date.parse(handle.mintedAt);
  if (Number.isNaN(mintedAt)) return true;
  return now.getTime() - mintedAt >= WA_REVIEW_HANDLE_ASSUMED_TTL_HOURS * 60 * 60 * 1000;
}

/* -------------------------------------------------------------------------- */
/* The two ways a stored asset becomes something on the wire                   */
/* -------------------------------------------------------------------------- */

/**
 * Asset + review handle → the media header slice 7's document already carries.
 *
 * **The only supported way to fill `waMediaHeaderSchema.mediaHandle`.** It takes
 * a {@link WaReviewHandle}, so a {@link WaSendMediaId} cannot reach that field:
 * the two share no value field and disagree on their discriminator, so the swap
 * is a type error rather than a template that approves and then sends nothing.
 *
 * `format` comes from the **asset**, not from a caller's argument, so a PDF
 * cannot be submitted as an image header by a call site that got its own
 * parameters in the wrong order.
 */
export function waMediaHeaderForReview(
  asset: WaHeaderMediaAsset,
  handle: WaReviewHandle,
): WaMediaHeader {
  return {
    format: asset.format,
    mediaHandle: handle.handle,
    fileName: asset.filename,
  };
}

/**
 * What one outbound message puts in its header component.
 *
 * A separate shape from {@link WaMediaHeader} because it is a separate thing:
 * the document's header describes the template Meta approved, this describes the
 * file *this* message carries. Meta's own API agrees — the first is
 * `example.header_handle` on `POST /<waba-id>/message_templates`, the second is
 * `components[].parameters[].<format>.id` on
 * `POST /<phone-number-id>/messages`.
 *
 * `filename` is carried for documents only, which is where Meta shows it.
 */
export const waSendHeaderMediaSchema = z
  .object({
    format: waMediaFormatSchema,
    media: waSendMediaIdSchema,
    filename: z.string().trim().min(1).max(255).optional(),
  })
  .strict();
export type WaSendHeaderMedia = z.infer<typeof waSendHeaderMediaSchema>;

/**
 * Asset + send media id → the header component of one message.
 *
 * The mirror of {@link waMediaHeaderForReview}, and the mirror is the point:
 * two constructors, each taking the one reference type its destination accepts,
 * and no path between them. A caller that has only a review handle and wants to
 * send must upload the stored bytes again — which it can, because we kept them.
 */
export function waSendHeaderMedia(
  asset: WaHeaderMediaAsset,
  media: WaSendMediaId,
): WaSendHeaderMedia {
  return {
    format: asset.format,
    media,
    ...(asset.format === 'document' ? { filename: asset.filename } : {}),
  };
}

/* -------------------------------------------------------------------------- */
/* The upload                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The format, as a query parameter on the upload route.
 *
 * A query parameter and not a multipart field for the reason slice 4 gives on
 * {@link import('./template.js').uploadTemplateAssetQuerySchema}: it has to be
 * readable *before* the body is parsed, because it chooses the byte ceiling, and
 * a limit that can only be applied after the file is buffered is not a limit.
 *
 * There is no `kind` here. This route stores one kind and it is not negotiable —
 * `kind` on slice 4's route existed to choose between two email kinds, and
 * offering it here would let a caller ask for `inline_image` on the WhatsApp
 * route and get slice 4's MIME list applied to a file bound for Meta.
 */
export const uploadWaHeaderMediaQuerySchema = z
  .object({ format: waMediaFormatSchema })
  .strict();
export type UploadWaHeaderMediaQuery = z.infer<typeof uploadWaHeaderMediaQuerySchema>;

/**
 * The non-file part of the multipart upload — empty, exactly as slice 4's is.
 * The file is checked against {@link WA_MEDIA_LIMITS} by
 * {@link findWaHeaderMediaRejection}, **by sniffing the bytes**, never by
 * trusting the client's `Content-Type`, which is a field the uploader chooses.
 */
export const uploadWaHeaderMediaRequestSchema = z.object({}).strict();
export type UploadWaHeaderMediaRequest = z.infer<typeof uploadWaHeaderMediaRequestSchema>;

/**
 * What the upload answers.
 *
 * The asset, always. The handle only if one was minted during the request, and
 * `reviewHandle` on the asset says so — a route that uploaded the bytes and
 * failed to reach Meta has still done the durable half of the job, and reporting
 * that as a failure would invite the caller to upload the file again.
 */
export const uploadWaHeaderMediaResponseSchema = z
  .object({ asset: waHeaderMediaAssetSchema })
  .strict();
export type UploadWaHeaderMediaResponse = z.infer<typeof uploadWaHeaderMediaResponseSchema>;

/** Addressing one template's header media. There is at most one per template. */
export const waHeaderMediaParamsSchema = z.object({ templateId: uuidSchema }).strict();
export type WaHeaderMediaParams = z.infer<typeof waHeaderMediaParamsSchema>;

/**
 * The header media a template currently has, or `null`.
 *
 * `null` is "this template has no media header" — which is true of every text
 * header and every header-less template — and it is deliberately not an empty
 * object. A screen showing an empty file card where the answer is "there is no
 * header" is the zero-versus-absent mistake in a different medium.
 */
export const waHeaderMediaResponseSchema = z
  .object({ asset: waHeaderMediaAssetSchema.nullable() })
  .strict();
export type WaHeaderMediaResponse = z.infer<typeof waHeaderMediaResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Meta's Resumable Upload, as a port shape                                    */
/* -------------------------------------------------------------------------- */

/**
 * What `POST /<app-id>/uploads` needs, in Meta's own field names.
 *
 * Here rather than in `apps/api` so the stub transport and the Graph transport
 * are held to one shape — the argument `meta-template-payload.ts` makes for the
 * submission body, applied to the upload. `fileLength` is Meta's `file_length`
 * and is the **exact** byte count; Meta refuses a session whose declared length
 * does not match what arrives, which is a good thing and the reason this is a
 * number on the request rather than something the transport measures later.
 *
 * `fileType` is typed as a plain string rather than an enum of the five values
 * Meta lists, because the value that belongs here is the type **sniffed from the
 * bytes** and already checked against {@link WA_MEDIA_LIMITS}. A second enum
 * here would be a second opinion about the same question, and the two would
 * drift in the direction of this one being more permissive.
 */
export const waResumableUploadRequestSchema = z
  .object({
    fileName: z.string().trim().min(1).max(255),
    fileLength: z.number().int().positive(),
    fileType: z.string().trim().min(1),
  })
  .strict();
export type WaResumableUploadRequest = z.infer<typeof waResumableUploadRequestSchema>;

/**
 * What the upload answers: Meta's `h`, wrapped the moment it arrives.
 *
 * Wrapped at the boundary rather than passed around as a string, so there is no
 * window in which a review handle is a bare `string` that could be assigned
 * somewhere a send id belongs.
 */
export const waResumableUploadResultSchema = z
  .object({ reviewHandle: waReviewHandleSchema })
  .strict();
export type WaResumableUploadResult = z.infer<typeof waResumableUploadResultSchema>;
